import { Trans, useTranslation } from 'react-i18next';
import referredLeadsTableStyles from '@modules/refer-and-earn/presentation/pages/List/features/ReferredLeadsTable/styles';
import {
  Box, Flex, Text
} from '@huspy/briks-web';
import useGetReferredLeadsList from '@modules/refer-and-earn/api/query/useGetReferredLeadsList';
import Table from '@components/v2/Table';
import { convertToDDMMYYYYFormat, formatToLocalTimeZone } from '@shared/utils/date-utils';
import { Badge } from '@components/Badge';
import { EmptyTemplateComponent } from '@modules/core/components/EmptyTemplateComponent';
import { Overlay } from '@mantine/core';
import useFeatureFlags from '@modules/core/hooks/useFeatureFlags';
import mockLeads from '@modules/refer-and-earn/api/mock/leads.json';

const { container, table } = referredLeadsTableStyles;

const colorStatusMap: Record<string, { textColor: string; backgroundColor: string }> = {
  New: { textColor: '#2655F5', backgroundColor: '#F1F6FF' },
  Contacted: { textColor: '#F59E0B', backgroundColor: '#FEF3C7' },
  'No Answer': { textColor: '#6B7280', backgroundColor: '#F3F4F6' },
  Pool: { textColor: '#8B5CF6', backgroundColor: '#F3E8FF' },
  Unqualified: { textColor: '#E33C5A', backgroundColor: '#FCECEF' },
  Qualified: { textColor: '#2EC4A0', backgroundColor: '#EAF9F6' },
};

const EmptyReferredLeads = () => {
  const { t } = useTranslation();

  return (
    <EmptyTemplateComponent
      title={ t('referAndEarn.list.empty.title') }
      description={ t('referAndEarn.list.empty.description') }
      cta={ null }
    />
  );
};

export const ReferredLeadsTable = () => {
  const { t } = useTranslation();
  const { isFeatureActive } = useFeatureFlags();
  const isFeatureActiveListTable = isFeatureActive('referAndEarn.listTable');
  const { data, isLoading } = useGetReferredLeadsList();

  if (data?.leads.length === 0 && !isLoading && isFeatureActiveListTable) {
    return <EmptyReferredLeads />;
  }

  const leads = isFeatureActiveListTable ? data?.leads! : mockLeads;

  return (
    <Flex direction='column' className={ container }>
      <Box>
        <Text fontSize='2xl' weight='medium' letterSpacing='-2'>{t('referAndEarn.list.table.title')}</Text>
        <Box paddingTop={ 1 }>
          <Text color='text-tertiary' letterSpacing='-0.8' fontSize='md'>{t('referAndEarn.list.table.subtitle')}</Text>
        </Box>
      </Box>
      <Flex className={ table }>
        <Table.Root>
          <Table.Head bg='blue.50'>
            <Table.Row>
              <Table.Header>{t('common.forms.name.label')}</Table.Header>
              <Table.Header>{t('common.forms.mobile.label')}</Table.Header>
              <Table.Header>{t('common.forms.marketType.label')}</Table.Header>
              <Table.Header>{t('common.forms.city.label')}</Table.Header>
              <Table.Header>{t('common.forms.status.label')}</Table.Header>
              <Table.Header>{t('common.forms.createdAt.label')}</Table.Header>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {isLoading
              ? <Table.Loading rows={ 4 } cells={ 6 } />
              : leads.map((lead) => (
                <Table.Row key={ lead.name }>
                  <Table.Cell>
                    {lead.name}
                  </Table.Cell>
                  <Table.Cell>
                    {lead.mobile}
                  </Table.Cell>
                  <Table.Cell>
                    {lead.market_type ? lead.market_type.charAt(0).toUpperCase() + lead.market_type.slice(1) : ''}
                  </Table.Cell>
                  <Table.Cell>
                    {lead.city}
                  </Table.Cell>
                  <Table.Cell>
                    <Badge
                      name={ lead.status }
                      backgroundColor={ colorStatusMap?.[lead?.status]?.backgroundColor }
                      textColor={ colorStatusMap?.[lead?.status]?.textColor }
                    />
                  </Table.Cell>
                  <Table.Cell>
                    {convertToDDMMYYYYFormat(formatToLocalTimeZone(lead.created_at))}
                  </Table.Cell>
                </Table.Row>
              ))}
          </Table.Body>
        </Table.Root>
        {!isFeatureActiveListTable && (
          <Overlay
            color='#FFF'
            backgroundOpacity={ 0.7 }
            blur={ 3 }
            center
            radius='16px'
            data-testid='refer-and-earn-overlay'
          >
            <Flex direction='column' align='center'>
              <Text fontSize='3xl' weight='medium' letterSpacing='-2'>{t('referAndEarn.list.table.overlay.title')}</Text>
              <Box paddingTop={ 1 }>
                <Text
                  color='text-tertiary'
                  letterSpacing='-0.8'
                  fontSize='lg'
                  textAlign='center'
                >
                  <Trans i18nKey='referAndEarn.list.table.overlay.subtitle' components={ { 1: <br /> } } />
                </Text>
              </Box>
            </Flex>
          </Overlay>
        )}
      </Flex>
    </Flex>
  );
};
